/**
 * QueryCircuitBreaker — per-surface failure tracker.
 *
 * - Records success/failure per key.
 * - After N failures within window → opens circuit (degraded).
 * - Half-open after cooldown → next success closes.
 * - Consumers can `shouldSkip(key)` to pause polling and rely on cache.
 */

type State = "closed" | "open" | "half_open";

type Surface = {
  key: string;
  state: State;
  failures: number;
  successes: number;
  lastFailAt: number;
  lastOkAt: number;
  openedAt: number;
};

const FAIL_THRESHOLD = 3;
const WINDOW_MS = 60_000;
const OPEN_COOLDOWN_MS = 30_000;

const map = new Map<string, Surface>();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function ensure(key: string): Surface {
  let s = map.get(key);
  if (!s) {
    s = { key, state: "closed", failures: 0, successes: 0, lastFailAt: 0, lastOkAt: 0, openedAt: 0 };
    map.set(key, s);
  }
  return s;
}

export function recordSuccess(key: string) {
  const s = ensure(key);
  s.successes += 1;
  s.lastOkAt = Date.now();
  if (s.state !== "closed") {
    s.state = "closed";
    s.failures = 0;
    s.openedAt = 0;
    emit();
  }
}

export function recordFailure(key: string) {
  const s = ensure(key);
  const now = Date.now();
  if (now - s.lastFailAt > WINDOW_MS) s.failures = 0;
  s.failures += 1;
  s.lastFailAt = now;
  if (s.failures >= FAIL_THRESHOLD && s.state !== "open") {
    s.state = "open";
    s.openedAt = now;
    emit();
  }
}

export function shouldSkip(key: string): boolean {
  const s = map.get(key);
  if (!s) return false;
  if (s.state === "open") {
    if (Date.now() - s.openedAt > OPEN_COOLDOWN_MS) {
      s.state = "half_open";
      emit();
      return false; // allow one probe
    }
    return true;
  }
  return false;
}

export function breakerSnapshot() {
  return Array.from(map.values()).map((s) => ({ ...s }));
}

export function isAnyOpen(): boolean {
  for (const s of map.values()) if (s.state === "open") return true;
  return false;
}

export function subscribeBreaker(fn: () => void) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** react-query retry helper — honors breaker (no retry storms). */
export function breakerRetry(key: string) {
  return (failureCount: number) => {
    if (failureCount >= 2) return false;
    if (shouldSkip(key)) return false;
    return true;
  };
}
